import { SimpleParseResolver } from './simple-parse-resolver';

/**
 * Checks the specified value to determine if it is not undefined or null and also not blank (when trimmed).
 * @param value The value to be checked
 * @return true if the specified value is not undefined or null, and also not blank (or just whitespace); otherwise false.
 *
 * The formula used to evaluate each variable type is:
 * <li>{null}      - always returns false</li>
 * <li>{undefined} - always returns false</li>
 * <li>NaN         - always returns false</li>
 * <li>Arguments   - true if value.length > 0</li>
 * <li>Array       - true if value.length > 0</li>
 * <li>Element     - passes Element.value back into this method</li>
 * <li>Boolean     - always returns true</li>
 * <li>Date        - always returns true</li>
 * <li>Function    - always returns true</li>
 * <li>Infinity    - always returns true</li>
 * <li>Object      - always returns true</li>
 * <li>Number      - always returns true</li>
 * <li>RegExp      - always returns true</li>
 * <li>String      - true if value.trim().length > 0</li>
 * <li>{unknown}   - always returns false</li>
 */
let hasValue = function (value: any): boolean {

  //undefined, null
  if (value == null) {
    return false;
  }

  if (typeof value === 'number') {
    //NaN
    if (isNaN(value)) {
      return false;
    }

    //If it's infinite, it registers as a number so no need to
    //do a special test for !isFinite()
    return true;
  }

  //string
  if (typeof value === 'string') {
    //This covers strings that are numbers as well.
    return value.trim().length > 0;
  }

  //boolean
  if (value === true || value === false) {
    return true;
  }

  if (typeof value === 'function') {
    return true;
  }

  if (typeof value === 'object') {
    //Array
    if ('length' in value) {
      return value.length > 0;
    }

    if (value instanceof HTMLElement) {
      return hasValue((<any>value).value);
    }

    //takes care of Date or RegExp obj as well
    return true;
  }

  return false;
};

/**
 * Compares two values using a recursive natural order (default is ascending order).  Options are available for descending order, ignoring the letter case and using a custom alphabet.
 *
 * The typical use of thie function is as a parameter to an Array's sort() method, such as:
 *      let z = ['File-10', 'File-12', 'File-20', 'File-2', 'File-21', 'File-100', 'File-99', 'File-1'];
 *      z.sort()                       --> ["File-1", "File-10", "File-100", "File-12", "File-2", "File-20", "File-21", "File-99"]
 *      z.sort(psUtils.naturalCompare) --> ["File-1", "File-2", "File-10", "File-12", "File-20", "File-21", "File-99", "File-100"]
 *
 * NOTE: There is a known issue where sorting a simple array that contain undefined values, these values are never passed into the compare function by the browser.
 *       This results in the undefined values always appearing last after the sort.  One work-around is to have the values in an object, and the array contains the objects.
 *
 * @param a The first value to compare
 * @param b The second value to compare
 * @see naturalCompareOptions for configuration options
 */
let naturalCompare = function (a: any, b: any): number {
  if (this.key != null) {
    a = a[this.key];
    b = b[this.key];
  }

  let alphabet = this.alphabet;
  let ignoreCase = this.isIgnoreCase;
  let isAscending = this.isAscending;
  let result: number;
  let i;
  let codeA;
  let codeB = 1;
  let posA = 0;
  let posB = 0;
  let getCode = (str, pos, code?) => {
    if (code) {
      for (i = pos; code = getCode(str, i), code < 76 && code > 65; ) {
        ++i;
      }
      return parseInt(str.slice(pos - 1, i), 10);
    }
    code = alphabet && alphabet.indexOf(str.charAt(pos));
    return (hasValue(code) && code > -1) ? code + 76 : ((code = str.charCodeAt(pos) || 0), code < 45 || code > 127) ? code
      : code < 46 ? 65          // -
        : code < 48 ? code - 1
          : code < 58 ? code + 18   // 0-9
            : code < 65 ? code - 11
              : code < 91 ? code + 11   // A-Z
                : code < 97 ? code - 37
                  : code < 123 ? code + 5   // a-z
                    : code - 63;
  };
  let isPseudoNullA = false;
  let isPseudoNullB = false;
  if (this.emptyAsNull) {
    isPseudoNullA = (a != null && !hasValue(a));
    isPseudoNullB = (b != null && !hasValue(b));
  }
  if (a == null || b == null || isPseudoNullA || isPseudoNullB) {
    let nulls = this.nulls;
    if ((a === undefined && b === undefined) || (a === null && b === null)) {
      result = 0;
    } else if (a === undefined) {
      result = ((b === null || isPseudoNullB) ? -1 :
          nulls === 'first' ? -1 :
            nulls === 'last' ? 1 :
              nulls === 'desc' ? (isAscending ? 1 : -1) :
                /* default */                   (isAscending ? -1 : 1)
      );
    } else if (b === undefined) {
      result = ((a === null || isPseudoNullA) ? 1 :
          nulls === 'first' ? 1 :
            nulls === 'last' ? -1 :
              nulls === 'desc' ? (isAscending ? -1 : 1) :
                /* default */                   (isAscending ? 1 : -1)
      );
    } else if (isPseudoNullA && isPseudoNullB) {
      result = a.localeCompare(b);
    } else if (a === null || isPseudoNullA) {
      result = (b === null ? 1 :
          isPseudoNullB ? -1 :
            nulls === 'first' ? -1 :
              nulls === 'last' ? 1 :
                nulls === 'desc' ? (isAscending ? 1 : -1) :
                  /* default */       (isAscending ? -1 : 1)
      );
    } else {
      result = (nulls === 'first' ? 1 :
          nulls === 'last' ? -1 :
            nulls === 'desc' ? (isAscending ? -1 : 1) :
              /* default */       (isAscending ? 1 : -1)
      );
    }
    return result;
  }

  // convert date to number
  if (a instanceof Date && b instanceof Date) {
    a = a.getTime();
    b = b.getTime();
  } else {
    // convert string A to number?
    if (typeof a === 'string') {
      let numA = parseFloat(a);
      if (!isNaN(numA)) {
        // coercion needed here
        if (a === String(numA) && a.length === numA.toString().length) {
          a = numA;
        }
      }
    }
    // convert string B to number?
    if (typeof b === 'string') {
      let numB = parseFloat(b);
      if (!isNaN(numB)) {
        // coercion needed here
        if (b === String(numB) && b.length === numB.toString().length) {
          b = numB;
        }
      }
    }
  }

  if (typeof a === 'number' && typeof b === 'number') {
    result = (a === b ? 0 : (a < b) ? -1 : 1);
    if (!isAscending) {
      result *= -1;
    }
    return result;
  } else {
    // using coercion below to handle identity instead of equality (comparing integers to strings)
    a += '';
    b += '';
    if (a !== b) {
      if (ignoreCase) {
        a = a.toLowerCase();
        b = b.toLowerCase();
      }
      for ( ; codeB; ) {
        codeA = getCode(a, posA++);
        codeB = getCode(b, posB++);
        if (codeA < 76 && codeB < 76 && codeA > 66 && codeB > 66) {
          codeA = getCode(a, posA, posA);
          codeB = getCode(b, posB, posA = i);
          posB = i;
        }
        if (codeA !== codeB) {
          result = (codeA < codeB) ? -1 : 1;
          if (!isAscending) {
            result *= -1;
          }
          return result;
        }
      }
    }
    return 0;
  }
};

/**
 * A configuration object to be used with naturalCompare.  Available options are:
 *      alphabet (String) an optional String containing the full list of characters, in the desired order.
 *          Estonian - "ABDEFGHIJKLMNOPRSŠZŽTUVÕÄÖÜXYabdefghijklmnoprsšzžtuvõäöüxy"
 *          Russian  - "АБВГДЕЁЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдеёжзийклмнопрстуфхцчшщъыьэюя"
 *      emptyAsNull  (boolean) a flag to indicate blank (or only white-space) values will be treated as null
 *      isAscending  (boolean) a flag to indicate if the sort should be in ascending order
 *      isIgnoreCase (boolean) a flag to indicate if the case of the letters should be ignored
 *      nulls (String) - how to handle undefined and null values in the sort order (undefined values are always before null values) - defaults to 'asc'
 *          first - null values are always first
 *          last  - null values are always last
 *          asc   - null values are first for ascending order and last for descending order (default)
 *          desc  - null values are last for ascending order and first for descending order
 */
export class PdsNaturalSort {

  //Assigned in constructor so it is bound to this object.
  naturalCompare: (a: any, b: any) => number;

  //configurations split regex
  CONFIG_SPLIT_REGEX = /^(\+{1,2}!{0,2}|\-{1,2}!{0,2}|\!{1,2}[+-]{0,2})?([^!^+^-].*)$/;

  alphabet: string | null = null;
  emptyAsNull = false;
  isAscending = true;
  isIgnoreCase = false;
  key: string | null = null;
  nulls = 'asc';

  constructor() {
    this.naturalCompare = naturalCompare.bind(this);
  }

  clearKey(): void {
    this.key = null;
  }

  setAlphabet(str: string): void {
    this.alphabet = (hasValue(str) ? str : null);
  }

  setAscending(value: boolean): void {
    this.isAscending = value;
  }

  setEmptyAsNull(value: boolean): void {
    this.emptyAsNull = value;
  }

  setIgnoreCase(value: boolean): void {
    this.isIgnoreCase = value;
  }

  setKey(value: string): void {
    this.key = value;
    if (value == null) {
      console.warn('Invalid value for natural compare key: ', value);
    }
  }

  setNulls(value): void {
    if (value == null || typeof value !== 'string') {
      value = 'asc';
    }
    value = value.toLowerCase();
    this.nulls = (value === 'desc' || value === 'first' || value === 'last' ? value : 'asc');
  }

  ascending(): void {
    this.isAscending = true;
  }

  descending(): void {
    this.isAscending = false;
  }

  ignoreCase(): void {
    this.isIgnoreCase = true;
  }

  nullsAsc(): void {
    this.nulls = 'asc';
  }

  nullsDesc(): void {
    this.nulls = 'desc';
  }

  nullsFirst(): void {
    this.nulls = 'first';
  }

  nullsLast(): void {
    this.nulls = 'last';
  }

  useCase(): void {
    this.isIgnoreCase = false;
  }

  reset(): void {
    this.alphabet = null;
    this.emptyAsNull = false;
    this.isAscending = true;
    this.isIgnoreCase = false;
    this.nulls = 'asc';
    this.clearKey();
  }

  /**
   * Sorts an array containing JavaScript objects, returning a new instance of the array sorted on the specified (nested) key.
   * Notes:
   *    The key cannot be nested within other objects (top-level).
   *    If "arr" is not an array, it will be returned.
   * @param arr The array to be sorted
   * @param key The name of the key within the object
   * @deprecated use sortObjectsAdvanced, sortObjects, or sortPrimatives
   */
  naturalSort(arr: Array<any>, key?: string): Array<any> {
    let result;
    if (arr instanceof Array) {
      let hasObject = false;
      let hasPrimitive = false;
      arr.forEach((o) => {
        if (o != null && typeof o === 'object') {
          hasObject = true;
        } else {
          hasPrimitive = true;
        }
      });
      if (hasObject && !hasPrimitive) {
        if (key == null) {
          throw 'Unable to sort an array of objects without a lookup key';
        }
        this.setKey(key);
        result = arr.sort(this.naturalCompare);
      } else if (!hasObject && hasPrimitive) {
        //arr.slice() clones the array.
        this.key = null; //There should be no key set for sorting primitives.
        result = arr.slice().sort(this.naturalCompare);
      } else if (!hasObject && !hasPrimitive) {
        result = arr;
      } else {
        throw 'Unable to sort an array containing both primitives and objects.';
      }
    } else {
      result = arr;
    }

    return result;
  }

  /**
   * This function does the same thing as sortObjects.  However, sometimes you need a custom function to extract data from
   * an object rather than a field key.  When that happens, use this function.
   *
   * @param items - unsorted list of objects.
   * @param fieldConfig - a list of configuration objects, each with a sortDirection and extractionKey.
   * @return A sorted array of objects.
   */
  sortObjectsAdvanced(items: Array<any> , fieldConfig: Array<{sortDirection?: string, extractionKey: ((value: any) => any) | string}>, reverseSort = false): Array<any> {
    let sortedArray: Array<any> = [];

    //clone the array and flag any undefined values.
    for (let i = 0; i < items.length; i++) {
      sortedArray[i] = items[i];
      if (typeof sortedArray[i] === 'undefined') {
        console.error('natural sort does not support undefined elements in the array.');
      }
    }
    let extractorConfigurations: Array<{extractor: any, isReversed: boolean, nullsFirst: boolean}> = [];

    for (let eachConfigurationSetting of fieldConfig) {
      let extractionKey = eachConfigurationSetting.extractionKey;
      let extractor;
      if (typeof extractionKey === 'function') {
        extractor = extractionKey;
      } else if (typeof extractionKey === 'string') {
        let resolver = new SimpleParseResolver(extractionKey);
        extractor = resolver.resolve.bind(resolver);
      }

      let extractorConfiguration = {
        extractor: extractor,
        isReversed: false,
        nullsFirst: true
      };

      switch (eachConfigurationSetting.sortDirection) {
        case '+!':
          extractorConfiguration.isReversed = reverseSort;
          extractorConfiguration.nullsFirst = false;
          break;
        case '!-':
          extractorConfiguration.isReversed = !reverseSort;
          extractorConfiguration.nullsFirst = false;
          break;
        case '-':
        case '-!':
          extractorConfiguration.isReversed = !reverseSort;
          //Needs to be the same as '+' so isReversed will swap it correctly.
          extractorConfiguration.nullsFirst = true;
          break;
        case '++':
        case '!++':
        case '!!++':
          extractorConfiguration.isReversed = false;
          extractorConfiguration.nullsFirst = true;
          break;
        case '!--':
        case '!!--':
          extractorConfiguration.isReversed = true;
          extractorConfiguration.nullsFirst = false;
          break;
        case '--':
        case '--!':
        case '--!!':
          extractorConfiguration.isReversed = true;
          extractorConfiguration.nullsFirst = true;
          break;
        case '++!':
        case '++!!':
          extractorConfiguration.isReversed = false;
          extractorConfiguration.nullsFirst = false;
          break;
        case '!!+':
          extractorConfiguration.isReversed = reverseSort;
          extractorConfiguration.nullsFirst = (extractorConfiguration.isReversed === true) ? false : true;
          break;
        case '+!!':
          extractorConfiguration.isReversed = reverseSort;
          extractorConfiguration.nullsFirst = (extractorConfiguration.isReversed === true) ? true : false;
          break;
        case '!!-':
          extractorConfiguration.isReversed = !reverseSort;
          extractorConfiguration.nullsFirst = (extractorConfiguration.isReversed === true) ? false : true;
          break;
        case '-!!':
          extractorConfiguration.isReversed = !reverseSort;
          extractorConfiguration.nullsFirst = (extractorConfiguration.isReversed === true) ? true : false;
          break;
        case '!':
        case '+':
        case '!!':
        case '!+':
        default:
          extractorConfiguration.isReversed = reverseSort;
          extractorConfiguration.nullsFirst = true;
          break;
      }

      extractorConfigurations.push(extractorConfiguration);
    }

    let comparator = (a, b) => {
      for (let eachExtractorConfiguration of extractorConfigurations) {
        let dataInA = eachExtractorConfiguration.extractor(a);
        let dataInB = eachExtractorConfiguration.extractor(b);
        if (eachExtractorConfiguration.nullsFirst) {
          this.nullsFirst();
        } else {
          this.nullsLast();
        }
        let result = this.naturalCompare(dataInA, dataInB);
        // if the result is 0 we want to keep continuing.
        if (result !== 0) {
          if (eachExtractorConfiguration.isReversed) {
            return result === 1 ? -1 : 1;
          } else {
            return result;
          }
        }
      }
      return 0;
    };
    sortedArray.sort(comparator);
    return sortedArray;
  }

  /**
   * this function takes an array of objects (primitives) and sorts it.
   * @param items - a set of dates, numbers, or strings to sort.
   * @param reverseSort - true or false depending on whether we should use the revese of the sorted array
   * @return sortedArray;
   */
  sortPrimitives(items: Array<string | number | Date | null>, reverseSort = false): Array<any> {
    let sortedArray: Array<any> = [];
    //clone the array and flag any undefined values.
    for (let i = 0; i < items.length; i++) {
      sortedArray[i] = items[i];
      if (typeof sortedArray[i] === 'undefined') {
        console.error('natural sort does not support undefined elements in the array.');
      }
    }
    let comparator = (a, b) => {
      let result = this.naturalCompare(a, b);
      if (result < 0) {
        let reversed = false;
        let r = reversed !== reverseSort ? 1 : - 1;
        return r;
      } else if (result > 0) {
        let reversed = false;
        let r = reversed !== reverseSort ? -1 : 1;
        return r;
      } else {
        return 0;
      }
    };
    return sortedArray.sort(comparator);
  }

  /**
   * Sort objects will take an list of objects to sort and a list of field configurations that contain information on
   * per field sort direction, and the extraction key. See https://uidocs.powerschool.com/tools/sort for configuration objects.
   * @param items - unsorted array of objects
   * @param fieldConfig - an array of field direction + field extraction keys.
   * Example 1: ['name.last', '-name.first'] means primary sort is in item.name.last, and a secondary sort on item.name.first (sorted descending)
   * Example 2: ['lastName', '++firstName'] means primary sort is item.lastName, secondarySort is item.firstName (always sorts ascending regardless of reverseSort)
   * @param reverseSort - boolean flag to reverse the sort.
   */
  sortObjects(items: Array<any>, fieldConfig: Array<string> | string, reverseSort = false): Array<any> {
    if (typeof fieldConfig === 'string') {
      fieldConfig = [ <string>fieldConfig ];
    }

    let sortedObjects;
    let enhancedConfigurations: Array<{sortDirection?: string, extractionKey: (data: any) => any | string}> = [];
    for (let eachConfigurationSetting of fieldConfig) {
      let sortDirectionAndExtractionKey = this.splitConfigurationSetting(eachConfigurationSetting);
      enhancedConfigurations.push(sortDirectionAndExtractionKey);
    }
    sortedObjects = this.sortObjectsAdvanced(items, enhancedConfigurations, reverseSort);
    return sortedObjects;
  }

  /**
   * This function breaks down a complex configuration setting, and turns it into a
   * sortDirection with the nulls included and returns the modified sortDirectionAndExtractionKey object
   * @param configurationSetting - a set of configuration settings like : ['!!-firstName', '!--firstName'] -->
   *
   * @return a simplified object like { sortDirection: '!!-', extractionkey: 'firstName'}
   */
  splitConfigurationSetting(configurationSetting: string): { sortDirection?: string, extractionKey: any } {
    let splitConfig = this.CONFIG_SPLIT_REGEX.exec(configurationSetting);
    let extractorConfig;
    if (splitConfig != null && splitConfig.length > 2) {
      extractorConfig = {
        sortDirection: splitConfig![1],
        extractionKey: splitConfig![2]
      };
    }
    return extractorConfig;
  }
}
